import {
  Label,
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import {
  CheckIcon,
  NoSymbolIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/solid';
import { useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

interface IOption {
  label: string;
  value: string;
  description?: string;
  color?: string;
  disabled?: boolean;
  displayText?: string;
  caption?: string;
}

interface IProps {
  label?: string;
  description?: string;

  options: IOption[];
  className?: string;
  error?: string;
  anchor?: any;
  optionWrapperClassName?: string;
  inputWrapperClassName?: string;
  dataTestId?: string;
}

interface IMultipleProps extends IProps {
  values: string[];
  onChange: (value: string[]) => void;
}

interface IComboboxProps extends IProps {
  value: string;
  onChange: (value: string) => void;
}

const comboboxClassNames = {
  root: 'w-full max-w-lg flex flex-col items-start justify-center gap-1',
  label: 'block text-sm font-medium text-gray-900',
  description: 'block text-sm text-gray-500',
  input:
    'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6',
  button:
    'absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none',
  buttonIcon: 'size-5 text-gray-400',
  error: 'border-red-500',
  optionWrapper:
    'z-50 max-h-60 w-fit overflow-visible rounded-sm bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm',
  option:
    'group relative cursor-default select-none py-1 pl-2 pr-9 text-gray-900 data-[focus]:bg-blue-400 data-[focus]:text-white data-[focus]:outline-none',
};

export function FormComboboxMultiple({
  label,
  description,
  values,
  onChange,
  options,
  className,
  error,
  anchor,
  optionWrapperClassName,
  dataTestId,
  inputWrapperClassName,
}: IMultipleProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<IOption[]>([]);

  const filtered =
    query === ''
      ? options
      : options.filter(
          (item) =>
            item.label.toLowerCase().includes(query.toLowerCase()) ||
            item.description?.toLowerCase().includes(query.toLowerCase()),
        );

  useEffect(() => {
    const newValues = values
      .map((value) => options.find((o) => o.value === value))
      .filter((v) => v) as IOption[];
    setSelected(newValues || []);
  }, [values, options]);

  return (
    <Combobox
      as="div"
      multiple
      className={twMerge([comboboxClassNames.root, className])}
      value={selected}
      onChange={(value) => {
        setQuery('');
        setSelected(value);
        onChange(value?.map((v: any) => v.value));
      }}
      data-testid={`${dataTestId}-combobox`}
    >
      {label && <Label className={comboboxClassNames.label}>{label}</Label>}
      {description && (
        <span className={comboboxClassNames.description}>{description}</span>
      )}
      <div className={twMerge(['relative w-full', inputWrapperClassName])}>
        <div className="relative">
          <ComboboxInput
            data-testid={`${dataTestId}-combobox-input`}
            className={twMerge([
              comboboxClassNames.input,
              className,
              error && comboboxClassNames.error,
            ])}
            onChange={(event) => setQuery(event.target.value)}
            onBlur={() => setQuery('')}
            displayValue={(item: IOption[]) =>
              item?.map((i: IOption) => i.displayText || i.label).join(', ')
            }
          />
          <ComboboxButton
            data-testid={`${dataTestId}-combobox-button`}
            className={comboboxClassNames.button}
          >
            <ChevronUpDownIcon
              className={comboboxClassNames.buttonIcon}
              aria-hidden="true"
            />
          </ComboboxButton>
        </div>
        {filtered.length > 0 && (
          <ComboboxOptions
            data-testid={`${dataTestId}-combobox-options`}
            anchor={anchor}
            className={twMerge([
              comboboxClassNames.optionWrapper,
              optionWrapperClassName,
            ])}
          >
            {filtered.map((item) => (
              <Option
                key={item.value}
                value={item}
                disabled={item.disabled || false}
                className={comboboxClassNames.option}
                dataTestId={dataTestId}
              />
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}

export function FormCombobox({
  label,
  description,
  value,
  onChange,
  options,
  className,
  inputWrapperClassName,
  error,
  anchor,
  optionWrapperClassName,
  dataTestId,
}: IComboboxProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<IOption | null>(null);

  const filtered =
    query === ''
      ? options
      : options.filter(
          (item) =>
            item.label.toLowerCase().includes(query.toLowerCase()) ||
            item.description?.toLowerCase().includes(query.toLowerCase()),
        );

  useEffect(() => {
    const newValues = options.find((o) => o.value === value);
    setSelected(newValues as IOption | null);
  }, [value, options]);

  return (
    <Combobox
      as="div"
      className={twMerge([comboboxClassNames.root, className])}
      value={selected}
      onChange={(newValue) => {
        setQuery('');
        setSelected(newValue as IOption | null);
        onChange(newValue?.value || '');
      }}
      data-testid={`${dataTestId}-combobox`}
    >
      {label && <Label className={comboboxClassNames.label}>{label}</Label>}
      {description && (
        <span className={comboboxClassNames.description}>{description}</span>
      )}
      <div className={twMerge(['relative w-full', inputWrapperClassName])}>
        <div className="relative">
          <ComboboxInput
            data-testid={`${dataTestId}-combobox-input`}
            className={twMerge([
              comboboxClassNames.input,
              className,
              error && comboboxClassNames.error,
            ])}
            onChange={(event) => setQuery(event.target.value)}
            onBlur={() => setQuery('')}
            displayValue={(item: IOption) => item?.displayText || item?.label}
          />
          <ComboboxButton
            data-testid={`${dataTestId}-combobox-button`}
            className={comboboxClassNames.button}
          >
            <ChevronUpDownIcon
              className={comboboxClassNames.buttonIcon}
              aria-hidden="true"
            />
          </ComboboxButton>
        </div>
        {filtered.length > 0 && (
          <ComboboxOptions
            anchor={anchor}
            data-testid={`${dataTestId}-combobox-options`}
            className={twMerge([
              comboboxClassNames.optionWrapper,
              optionWrapperClassName,
            ])}
          >
            {filtered.map((item) => (
              <Option
                key={item.value}
                value={item}
                disabled={item.disabled || false}
                className={comboboxClassNames.option}
                dataTestId={dataTestId}
              />
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}

function Option({
  value,
  disabled,
  className,
  dataTestId,
}: {
  value: IOption;
  dataTestId?: string;
  disabled?: boolean;
  className: string;
}) {
  return (
    <ComboboxOption
      value={value}
      disabled={disabled}
      data-testid={`${dataTestId}-combobox-option-${value.value}`}
      className={twMerge([
        comboboxClassNames.option,
        disabled && 'cursor-not-allowed opacity-50',
        className,
      ])}
    >
      <div
        title={`${value.label}${value.description ? ` - ${value.description}` : ''}`}
        className="flex items-center"
      >
        {value.color && (
          <span
            className={twMerge(['inline-block size-2 shrink-0 rounded-full'])}
            style={{ backgroundColor: value.color }}
            aria-hidden="true"
          />
        )}
        <span className="ml-3 truncate group-data-[selected]:font-semibold">
          {value.label}
        </span>
        {value.description && (
          <span className="ml-2 flex-1 truncate text-gray-500 group-data-[focus]:text-indigo-200">
            {value.description}
          </span>
        )}
        {value.caption && (
          <span className="ml-2 flex-1 truncate text-gray-500 group-data-[focus]:text-indigo-200">
            {value.caption}
          </span>
        )}
      </div>
      <span className="absolute inset-y-0 right-0 hidden items-center pr-4 pt-1 text-red-500 group-data-[disabled]:flex">
        <NoSymbolIcon className="size-5" aria-hidden="true" />
      </span>
      <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-blue-400 group-data-[selected]:flex group-data-[focus]:text-white">
        <CheckIcon className="size-5" aria-hidden="true" />
      </span>
    </ComboboxOption>
  );
}
